import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { billingApi, type BillingInvoiceView, type InvoiceState } from '../../api/client';
import { useTenant } from '../../components/Shell';
import { useAuth } from '../../lib/auth';
import { billingShown } from '../../lib/portal';
import { Button, Card, Chip, Empty, Field, Kpi, LoadError, Loading, Notice, PageHeader } from '../../components/ui';
import type { Tone } from '../../lib/format';
import { Icon } from '../../lib/icons';
import { useData } from '../../lib/useData';
import { PaymentMethodsCard } from '../../components/PaymentMethods';
import { PayInvoiceCard, PaymentBanner } from '../../components/PayInvoice';

/**
 * Billing (1.13.0, Teampace 3099): the company's invoices (the same as the PDF that was emailed),
 * what's due, the next bill as things stand, who is on which plan, who gets the invoices, and the
 * billing address. For admins, Stitch staff, and anyone whose role includes Billing (a bookkeeper).
 * Payment methods and autopay (3092) are in PaymentMethodsCard; paying an invoice (3091) in PayInvoiceCard,
 * and an invoice whose payment failed or is past due in PaymentBanner.
 *
 * Until Stitch staff turn "Customers see Billing" on (1.13.0), the company's own people get BillingSoon
 * instead (its routes answer 404 BILLING_NOT_LIVE to them); staff see it as it will be.
 */

/** $1,234.56 (and −$8.00). */
export function money(cents: number): string {
  const s = (Math.abs(cents) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${cents < 0 ? '−' : ''}$${s}`;
}

function rate(cents: number): string {
  if (Number.isInteger(cents)) return money(cents);
  let s = (Math.abs(cents) / 100).toFixed(4).replace(/0+$/, '');
  if (/\.\d$/.test(s)) s += '0';
  return `${cents < 0 ? '−' : ''}$${s}`;
}

/** "Dec 1, 2026" from a day. */
function day(d: string | null | undefined): string {
  if (!d) return '';
  const [y, m, dd] = d.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, dd)).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

function monthOf(d: string): string {
  const [y, m] = d.split('-').map(Number) as [number, number];
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

const STATE: Record<InvoiceState, { label: string; tone: Tone }> = {
  paid: { label: 'Paid', tone: 'ok' },
  open: { label: 'Open', tone: 'neutral' },
  overdue: { label: 'Overdue', tone: 'crit' },
  void: { label: 'Void', tone: 'neutral' }
};

const KIND: Record<string, string> = { first: 'First invoice', monthly: 'Monthly', final: 'Final invoice' };

/** Whether this person sees the company's Billing yet (staff always do). */
function useBillingShown(domain: string): boolean {
  const { state } = useAuth();
  return billingShown(state?.user?.staff, state?.memberships?.find((m) => m.tenant === domain.toLowerCase()));
}

/** Before billing goes live: a plain page, so a bookkeeper (whose only page this is) isn't shown an empty app. */
function BillingSoon() {
  return (
    <div className="content billing">
      <PageHeader eyebrow="Company" title="Billing" />
      <Card>
        <Empty title="Billing in Stitch starts soon">Your invoices will show here.</Empty>
      </Card>
    </div>
  );
}

export function TenantBilling() {
  const { tenant } = useTenant();
  return useBillingShown(tenant.domain) ? <BillingOverview /> : <BillingSoon />;
}

function BillingOverview() {
  const { tenant } = useTenant();
  const d = useData(() => billingApi.overview(tenant.domain), [tenant.domain], { live: false });
  if (d.error) return <LoadError error={d.error} retry={d.reload} />;
  if (!d.data) return <Loading />;
  const o = d.data;
  return (
    <div className="content billing">
      <PageHeader eyebrow="Company" title="Billing" />
      <PaymentBanner domain={tenant.domain} inline />
      {!o.account ? (
        <Card>
          <Empty title="Nothing to show yet">Your company’s invoices from Stitch will be here, with what’s due and who gets them.</Empty>
        </Card>
      ) : (
        <>
          <section className="kpis" aria-label="Billing">
            <Kpi label="Due now" value={money(o.balanceCents ?? 0)} foot={(o.balanceCents ?? 0) > 0 ? 'Across your open invoices' : 'Nothing due'} />
            <Kpi
              label={o.next ? `Next bill (${monthOf(o.next.period)})` : 'Next bill'}
              value={o.next ? money(o.next.totalCents) : '—'}
              foot={o.next ? 'An estimate, before usage' : o.account.cancelAt ? `Service ends ${day(o.account.cancelAt)}` : 'Not yet'}
            />
            <Kpi label="Account" value={<span className="mono">{o.account.number ?? '—'}</span>} small foot={o.account.net30 ? 'Net 30, by invoice' : 'Billed on the 1st'} />
          </section>

          <Card title="Invoices">
            {o.invoices && o.invoices.length ? (
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Invoice</th>
                      <th>For</th>
                      <th>Issued</th>
                      <th>Due</th>
                      <th className="num">Amount</th>
                      <th>Status</th>
                      <th aria-label="Files" />
                    </tr>
                  </thead>
                  <tbody>
                    {o.invoices.map((i) => (
                      <tr key={i.id}>
                        <td>
                          <Link className="mono" to={`/t/${encodeURIComponent(tenant.domain)}/billing/invoices/${encodeURIComponent(i.id)}`}>
                            {i.number}
                          </Link>
                        </td>
                        <td>{i.kind === 'first' ? `Rest of ${monthOf(i.period)}` : `${monthOf(i.period)}${i.kind === 'final' ? ' (final)' : ''}`}</td>
                        <td>{day(i.issuedOn)}</td>
                        <td>{day(i.dueOn)}</td>
                        <td className="num">{money(i.dueCents)}</td>
                        <td>
                          <Chip tone={STATE[i.state].tone} small>
                            {STATE[i.state].label}
                          </Chip>
                          {(i.state === 'open' || i.state === 'overdue') && i.paidCents > 0 && <span className="muted small"> {money(i.dueCents - i.paidCents)} left</span>}
                        </td>
                        <td className="num">
                          <a className="link-btn" href={billingApi.fileUrl(tenant.domain, i.id, 'pdf')} download>
                            PDF
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <Empty title="No invoices yet">Your first invoice will be here as soon as it’s issued.</Empty>
            )}
          </Card>

          <div className="cols">
            <div className="col-main">
              {o.next && (
                <Card title={`Your next bill: ${monthOf(o.next.period)}`}>
                  <p className="muted">As things stand today. It changes as people, plans and numbers change; usage is billed once the month ends, so it isn’t here yet.</p>
                  <dl className="billing-kv">
                    {o.next.sections.map((s) => (
                      <div key={s.id}>
                        <dt>{s.title}</dt>
                        <dd className="num">{money(s.totalCents)}</dd>
                      </div>
                    ))}
                    <div>
                      <dt>
                        <strong>Estimate</strong>
                      </dt>
                      <dd className="num">
                        <strong>{money(o.next.totalCents)}</strong>
                      </dd>
                    </div>
                  </dl>
                </Card>
              )}
              <Card title="Plans and seats">
                {o.plans && o.plans.length ? (
                  <div className="table-wrap">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Plan or add-on</th>
                          <th className="num">How many</th>
                          <th>Who or what</th>
                        </tr>
                      </thead>
                      <tbody>
                        {o.plans.map((p) => (
                          <tr key={p.item}>
                            <td>{p.name}</td>
                            <td className="num">{p.count}</td>
                            <td className="muted">{p.who.slice(0, 8).join(', ') + (p.who.length > 8 ? ` and ${p.who.length - 8} more` : '')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <Empty title="Nothing yet">People and shared phones show here once they’ve signed in.</Empty>
                )}
              </Card>
            </div>
            <div className="col-side">
              <Contacts domain={tenant.domain} contacts={o.contacts ?? []} recipients={o.recipients ?? []} onSaved={d.reload} />
              <PaymentMethodsCard domain={tenant.domain} />
              <Address domain={tenant.domain} billTo={o.account.billTo} onSaved={d.reload} />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Contacts({ domain, contacts, recipients, onSaved }: { domain: string; contacts: { email: string; name: string }[]; recipients: { email: string; name: string }[]; onSaved: () => void }) {
  const [list, setList] = useState(contacts.length ? contacts : [{ email: '', name: '' }]);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const save = async () => {
    setError('');
    setSaved(false);
    try {
      await billingApi.setContacts(
        domain,
        list.filter((c) => c.email.trim()).map((c) => ({ email: c.email.trim(), name: c.name.trim() }))
      );
      setSaved(true);
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };
  return (
    <Card title="Who gets invoices">
      <p className="muted">
        Each invoice is emailed here with its PDF. They don’t need to sign in to Stitch.
        {!contacts.length && recipients.length > 0 && ` Until you add someone, invoices go to ${recipients.map((r) => r.email).join(', ')}.`}
      </p>
      {list.map((c, i) => (
        <div key={i} className="billing-fields">
          <Field label={i === 0 ? 'Email' : ''} aria-label="Email" type="email" value={c.email} onChange={(e) => setList(list.map((x, j) => (j === i ? { ...x, email: e.target.value } : x)))} placeholder="ap@yourcompany.com" />
          <Field label={i === 0 ? 'Name (optional)' : ''} aria-label="Name" value={c.name} onChange={(e) => setList(list.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} />
        </div>
      ))}
      <div className="billing-actions">
        {list.length < 10 && (
          <Button variant="ghost" icon="plus" onClick={() => setList([...list, { email: '', name: '' }])}>
            Add someone
          </Button>
        )}
        <span className="grow" />
        <Button variant="primary" onClick={() => void save()}>
          Save
        </Button>
      </div>
      {saved && <Notice tone="ok">Saved. The next invoice goes to them.</Notice>}
      {error && <Notice tone="crit">{error}</Notice>}
    </Card>
  );
}

function Address({ domain, billTo, onSaved }: { domain: string; billTo: Record<string, string>; onSaved: () => void }) {
  const [a, setA] = useState({ attn: billTo.attn ?? '', line1: billTo.line1 ?? '', line2: billTo.line2 ?? '', city: billTo.city ?? '', state: billTo.state ?? '', zip: billTo.zip ?? '', email: billTo.email ?? '' });
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const set = (k: keyof typeof a) => (e: { target: { value: string } }) => setA({ ...a, [k]: e.target.value });
  return (
    <Card title="Billing address">
      <p className="muted">Shown on your invoices. Taxes follow where your service is, not this address.</p>
      <Field label="Attention (optional)" value={a.attn} onChange={set('attn')} placeholder="Accounts Payable" />
      <Field label="Street address" value={a.line1} onChange={set('line1')} />
      <Field label="Suite or floor (optional)" value={a.line2} onChange={set('line2')} />
      <div className="billing-fields">
        <Field label="City" value={a.city} onChange={set('city')} />
        <Field label="State" value={a.state} onChange={set('state')} maxLength={2} placeholder="MD" />
        <Field label="ZIP" value={a.zip} onChange={set('zip')} inputMode="numeric" />
      </div>
      <div className="billing-actions">
        <span className="grow" />
        <Button
          variant="primary"
          onClick={async () => {
            setError('');
            setSaved(false);
            try {
              await billingApi.setAddress(domain, a);
              setSaved(true);
              onSaved();
            } catch (e) {
              setError(e instanceof Error ? e.message : String(e));
            }
          }}
        >
          Save address
        </Button>
      </div>
      {saved && <Notice tone="ok">Saved. It’s on your next invoice.</Notice>}
      {error && <Notice tone="crit">{error}</Notice>}
    </Card>
  );
}

/** One invoice, as its PDF shows it, with the PDF and usage file to download. */
export function BillingInvoice() {
  const { tenant } = useTenant();
  return useBillingShown(tenant.domain) ? <InvoicePage /> : <BillingSoon />;
}

function InvoicePage() {
  const { tenant } = useTenant();
  const { id = '' } = useParams();
  const d = useData(() => billingApi.invoice(tenant.domain, id), [tenant.domain, id], { live: false });
  if (d.error) return <LoadError error={d.error} retry={d.reload} />;
  if (!d.data) return <Loading />;
  const inv: BillingInvoiceView = d.data.invoice;
  const back = `/t/${encodeURIComponent(tenant.domain)}/billing`;
  const totals: [string, number][] = [['Charges', inv.subtotalCents]];
  if (inv.feeCents) totals.push(['Fees', inv.feeCents]);
  if (inv.taxCents) totals.push(['Taxes', inv.taxCents]);
  totals.push(['Total', inv.totalCents]);
  if (inv.creditCents) totals.push(['Account credit used', -inv.creditCents]);
  if (inv.carryCents) totals.push(['Carried to your next invoice (under $1)', -inv.carryCents]);
  return (
    <div className="content billing invoice-page">
      <PageHeader
        eyebrow={
          <Link to={back}>
            <Icon name="chevLeft" size={14} /> Billing
          </Link>
        }
        title={`Invoice ${inv.number ?? ''}`}
        chips={
          <Chip tone={STATE[inv.state].tone} small>
            {STATE[inv.state].label}
          </Chip>
        }
        actions={
          <>
            <a className="btn secondary" href={billingApi.fileUrl(tenant.domain, inv.id, 'csv')} download>
              <Icon name="download" size={17} strokeWidth={2.2} />
              Usage file
            </a>
            <a className="btn primary" href={billingApi.fileUrl(tenant.domain, inv.id, 'pdf')} download>
              <Icon name="download" size={17} strokeWidth={2.2} />
              PDF
            </a>
          </>
        }
      />
      {inv.voided && <Notice tone="warn" title="This invoice is void">{inv.voided.reason}. Nothing is owed on it; its days are on a later invoice.</Notice>}
      <section className="kpis" aria-label="This invoice">
        <Kpi label="Amount due" value={money(inv.dueCents)} foot={inv.paidCents > 0 ? `${money(inv.paidCents)} paid` : inv.dueOn ? `Due ${day(inv.dueOn)}` : ''} />
        <Kpi label="Issued" value={day(inv.issuedOn)} small foot={KIND[inv.kind] ?? ''} />
        {inv.service && <Kpi label="Service" value={`${day(inv.service.from)} – ${day(inv.service.to)}`} small />}
        {inv.usage && <Kpi label="Usage" value={`${day(inv.usage.from)} – ${day(inv.usage.to)}`} small />}
      </section>
      {inv.sections.map((s) => (
        <Card key={s.id} title={s.title} action={s.sub ? <span className="muted small">{s.sub}</span> : undefined}>
          <div className="table-wrap">
            <table className="table invoice-lines">
              <thead>
                <tr>
                  <th>Description</th>
                  <th className="num">Qty</th>
                  <th className="num">Rate</th>
                  <th className="num">Amount</th>
                </tr>
              </thead>
              <tbody>
                {s.lines.map((l, i) => (
                  <tr key={i}>
                    <td>
                      <span className="cell-stack">
                        <strong>{l.description}</strong>
                        {l.note && <small className="muted">{l.note}</small>}
                      </span>
                    </td>
                    <td className="num">{l.qty === null ? ((l.kind === 'tax' || l.kind === 'fee') ? '' : '—') : l.qty.toLocaleString('en-US', { maximumFractionDigits: 2 })}</td>
                    <td className="num">{l.rateLabel ?? (l.rateCents === null ? ((l.kind === 'tax' || l.kind === 'fee') ? '' : '—') : rate(l.rateCents))}</td>
                    <td className="num">{money(l.amountCents)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <th colSpan={3}>{s.title} total</th>
                  <th className="num">{money(s.totalCents)}</th>
                </tr>
              </tfoot>
            </table>
          </div>
        </Card>
      ))}
      <Card title="Totals">
        <dl className="billing-kv">
          {totals.map(([k, c]) => (
            <div key={k}>
              <dt>{k}</dt>
              <dd className="num">{money(c)}</dd>
            </div>
          ))}
          <div>
            <dt>
              <strong>Amount due</strong>
            </dt>
            <dd className="num">
              <strong>{money(inv.dueCents)}</strong>
            </dd>
          </div>
        </dl>
        {inv.creditNotes.length > 0 && (
          <Notice title="Credit notes">
            {inv.creditNotes.map((n) => `${n.number}: ${money(n.totalCents)} (${n.reason})`).join('; ')}. Credit notes go to your account’s credit, used on your next invoice.
          </Notice>
        )}
        {inv.paidCents !== 0 && (
          <p className="muted small">
            {money(inv.paidCents)} paid{inv.paidCents < inv.dueCents ? `; ${money(inv.dueCents - inv.paidCents)} left to pay` : ''}.
          </p>
        )}
      </Card>
      {!inv.voided && <PayInvoiceCard domain={tenant.domain} invoiceId={inv.id} onPaid={d.reload} />}
    </div>
  );
}
